// src/components/hotelRoomCategories/HotelRoomCategoriesTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetHotelRoomCategories from "../../hooks/hotelRoomCategories/useGetHotelRoomCategories";
import useDeleteHotelRoomCategory from "../../hooks/hotelRoomCategories/useDeleteHotelRoomCategory";
import useDeleteAllHotelRoomCategories from "../../hooks/hotelRoomCategories/useDeleteAllHotelRoomCategories";
import useRestoreHotelRoomCategory from "../../hooks/hotelRoomCategories/useRestoreHotelRoomCategory";
import useRestoreAllHotelRoomCategories from "../../hooks/hotelRoomCategories/useRestoreAllHotelRoomCategories";
import useForceDeleteHotelRoomCategory from "../../hooks/hotelRoomCategories/useForceDeleteHotelRoomCategory";
import useForceDeleteAllHotelRoomCategories from "../../hooks/hotelRoomCategories/useForceDeleteAllHotelRoomCategories";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import HotelRoomCategoriesTableRow from "./Table/HotelRoomCategoriesTableRow";
import HotelRoomCategoriesTableColumns from "./Table/HotelRoomCategoriesTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const HotelRoomCategoriesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("hotel_room_categories_list"),
            create: has("hotel_room_categories_create"),
            edit: has("hotel_room_categories_edit"),
            delete: has("hotel_room_categories_delete"),
            forceDelete: has("hotel_room_categories_force_delete"),
            archiveList: has("hotel_room_categories_archive_list"),
            restore: has("hotel_room_categories_restore"),
            restoreAll: has("hotel_room_categories_restore-all-table"),
            deleteAll: has("hotel_room_categories_delete-all-table"),
            massForceDeleteAll: has("hotel_room_categories_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/hotel-room-categories/archive");

    // CRUD Hooks
    const { deleteHotelRoomCategories, isDelete } = useDeleteHotelRoomCategory();
    const { deleteAllHotelRoomCategories, isDeleteAll } = useDeleteAllHotelRoomCategories();
    const { restoreHotelRoomCategory, isRestore } = useRestoreHotelRoomCategory();
    const { restoreAllHotelRoomCategories, isRestoreAll } = useRestoreAllHotelRoomCategories();
    const { forceDeleteHotelRoomCategory, isForceDelete } = useForceDeleteHotelRoomCategory();
    const { forceDeleteAllHotelRoomCategories, isForceDeleteAll } = useForceDeleteAllHotelRoomCategories();

    // ✅ استخدام Custom Hook للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/hotel-room-categories",
        deleteSingle: deleteHotelRoomCategories,
        deleteAll: deleteAllHotelRoomCategories,
        restoreSingle: restoreHotelRoomCategory,
        restoreAll: restoreAllHotelRoomCategories,
        forceDeleteSingle: forceDeleteHotelRoomCategory,
        forceDeleteAll: forceDeleteAllHotelRoomCategories,
        isArchive,
    });

    // ✨ Local State (specific to this table)
    const [statusFilter, setStatusFilter] = React.useState("");

    // ✨ Updated filters to include status and sorting
    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    // ✨ Filter configuration for TableFilterBar
    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    // ✨ Clear all filters handler
    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, hotelRoomCategories, meta } = useGetHotelRoomCategories(
        page,
        perPage,
        filters
    );

    const items = useMemo(() => hotelRoomCategories?.items || [], [hotelRoomCategories]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <HotelRoomCategoriesTableRow
            key={params.item.id}
            hotelRoomCategory={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive
        ? !perms.massForceDeleteAll
        : !perms.deleteAll;

    const title = isArabic ? "فئات الغرف" : "Hotel Room Categories";
    const subtitle = isArchive
        ? isArabic
            ? "عرض فئات الغرف المحذوفة (الأرشيف)"
            : "Archived hotel room categories (soft deleted)"
        : isArabic
            ? "إدارة فئات الغرف المتاحة في النظام"
            : "Manage available hotel room categories in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة فئة غرفة"
            : "Create new hotel room category";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للاستعادة"
                        : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllHotelRoomCategories({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic
                        ? "ليس لديك صلاحية للإنشاء"
                        : "No create permission"
                );
                return;
            }

            navigate("/hotel-room-categories/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/hotel-room-categories/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/hotel-room-categories"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={HotelRoomCategoriesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={
                    isArchive ? "لا توجد فئات غرف في الأرشيف" : "لا توجد فئات غرف"
                }
                emptyLabelEn={isArchive ? "No archived hotel room categories" : "No hotel room categories"}
                loadErrorAr="حدث خطأ أثناء تحميل فئات الغرف"
                loadErrorEn="Failed to load hotel room categories"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default HotelRoomCategoriesTable;
